/* Admin: Posts list + post editor (blog), backed by Firestore. */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Link, navigate } from "../../lib/router-compat.jsx";
import AdminShell from "../../components/shell/AdminShell.jsx";
import {
  Button, Field, Input, Textarea, Card, Badge, EmptyState, Skeleton,
  ConfirmDialog, Icon, MediaUploaderField, RichTextEditor, showToast,
} from "../../ui/index.js";
import { usePosts, usePost } from "../../data/hooks.js";
import { savePost, deletePost } from "../../data/mutations.js";
import { formatRelative, slugify } from "../../utils/format.js";

export function AdminPostsListPage() {
  const { posts, loading } = usePosts();
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [deleting, setDeleting] = useState(null);

  const filtered = posts.filter((p) => {
    if (filter === "drafts" && p.status !== "draft") return false;
    if (filter === "published" && p.status !== "published") return false;
    if (search && !p.title?.toLowerCase().includes(search.toLowerCase()) && !p.authorName?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const doDelete = async () => {
    try {
      await deletePost(deleting.id, deleting.featuredImage?.storagePath);
      showToast({ intent: "success", title: "Post deleted" });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't delete", body: err.message });
    }
    setDeleting(null);
  };

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Posts</h1><p>Write, edit, and publish to the blog.</p></div>
        <Link to="/admin/posts/new" className="btn btn--primary"><Icon.Plus size={16} />New post</Link>
      </div>

      <div className="row row--between" style={{ marginBottom: 24 }}>
        <div className="chips">
          <button className={`chip ${filter === "all" ? "active" : ""}`} onClick={() => setFilter("all")}>All ({posts.length})</button>
          <button className={`chip ${filter === "drafts" ? "active" : ""}`} onClick={() => setFilter("drafts")}>Drafts ({posts.filter((p) => p.status === "draft").length})</button>
          <button className={`chip ${filter === "published" ? "active" : ""}`} onClick={() => setFilter("published")}>Published ({posts.filter((p) => p.status === "published").length})</button>
        </div>
        <div style={{ position: "relative", width: 280 }}>
          <Icon.Search size={16} style={{ position: "absolute", left: 12, top: 12, color: "var(--text-muted)" }} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search posts..." style={{ paddingLeft: 36 }} />
        </div>
      </div>

      {loading ? (
        <Skeleton h={240} />
      ) : filtered.length === 0 ? (
        <Card>
          <EmptyState icon={<Icon.FileText size={40} />} title={posts.length === 0 ? "No posts yet." : "No posts match this filter."} body={posts.length === 0 ? "Start with a draft. Publish whenever you're ready." : "Try clearing your filter or search."} action={posts.length === 0 && <Link to="/admin/posts/new" className="btn btn--primary"><Icon.Plus size={16} />Write your first post</Link>} />
        </Card>
      ) : (
        <table className="table">
          <thead><tr><th>Title</th><th style={{ width: 140 }}>Status</th><th style={{ width: 180 }}>Author</th><th style={{ width: 160 }}>Last updated</th><th className="table__actions">Actions</th></tr></thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="clickable" onClick={() => navigate(`/admin/posts/${p.id}/edit`)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{p.title}</div>
                  <div className="t-xs muted" style={{ marginTop: 2 }}>{(p.excerpt || "").slice(0, 90)}</div>
                </td>
                <td>
                  <Badge variant={p.status === "published" ? "published" : "draft"}>{p.status === "published" ? "Published" : "Draft"}</Badge>
                  {p.authoredByMinor && <Badge variant="minor" style={{ marginLeft: 6 }} noDot>Minor</Badge>}
                </td>
                <td className="t-sm">{p.authorName}</td>
                <td className="t-sm muted">{formatRelative(p.updatedAt)}</td>
                <td className="table__actions" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/posts/${p.id}/edit`)} aria-label="Edit"><Icon.Pencil size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(p)} aria-label="Delete"><Icon.Trash size={14} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <ConfirmDialog open={!!deleting} title={`Delete '${deleting?.title}'?`} body="This cannot be undone." confirmLabel="Delete" destructive onConfirm={doDelete} onClose={() => setDeleting(null)} />
    </AdminShell>
  );
}

const EMPTY_POST = { slug: "", status: "draft", title: "", excerpt: "", bodyHtml: "", featuredImage: null, authorName: "", authoredByMinor: false, publishedAt: null };

export function AdminPostEditorPage({ isNew }) {
  const { postId } = useParams();
  const { post: remote, loading } = usePost(isNew ? null : postId);
  const [post, setPost] = useState(EMPTY_POST);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);
  const seeded = useRef(false);

  useEffect(() => {
    if (isNew) { seeded.current = true; return; }
    if (!loading && remote && !seeded.current) { setPost({ ...EMPTY_POST, ...remote }); setSlugTouched(true); seeded.current = true; }
  }, [isNew, loading, remote]);

  const set = (k, v) => setPost((p) => ({ ...p, [k]: v }));
  const onTitle = (e) => {
    const title = e.target.value;
    setPost((p) => ({ ...p, title, slug: slugTouched ? p.slug : slugify(title) }));
  };

  const persist = async (status) => {
    if (!post.title.trim()) { showToast({ intent: "danger", title: "Add a title first" }); return; }
    setSaving(true);
    try {
      const data = {
        slug: post.slug || slugify(post.title),
        status,
        title: post.title.trim(),
        excerpt: post.excerpt,
        bodyHtml: post.bodyHtml,
        featuredImage: post.featuredImage,
        authorName: post.authorName || "Purple Angels Kids Foundation",
        authoredByMinor: !!post.authoredByMinor,
        publishedAt: status === "published" ? (post.publishedAt || new Date().toISOString()) : post.publishedAt || null,
      };
      const id = await savePost(isNew ? null : postId, data);
      showToast({ intent: "success", title: status === "published" ? "Post published" : "Draft saved" });
      if (isNew) navigate(`/admin/posts/${id}/edit`, { replace: true });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't save", body: err.message });
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && loading && !seeded.current) return <AdminShell><Skeleton h={400} /></AdminShell>;

  return (
    <AdminShell>
      <Link to="/admin/posts" className="back-link"><Icon.ArrowLeft size={14} />Back to posts</Link>
      <div className="savebar" style={{ marginTop: 8 }}>
        <h1 className="t-lg" style={{ margin: 0, flex: 1 }}>{isNew ? "New post" : "Editing post"}</h1>
        <Badge variant={post.status === "published" ? "published" : "draft"}>{post.status === "published" ? "Published" : "Draft"}</Badge>
        <Button variant="secondary" loading={saving} onClick={() => persist("draft")}>Save draft</Button>
        <Button variant="primary" loading={saving} onClick={() => persist("published")}>{post.status === "published" ? "Update" : "Publish"}</Button>
      </div>

      <div className="dashboard-split" style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 32, marginTop: 24 }}>
        <div className="stack-lg">
          <Field label="Title"><Input size="lg" value={post.title} onChange={onTitle} placeholder="Post title" /></Field>
          <Field label="URL slug" helper="Used in the link: /blog/your-slug"><Input value={post.slug} onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }} /></Field>
          <Field label="Excerpt" helper="A short summary shown in the blog list."><Textarea value={post.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} /></Field>
          <Field label="Body"><RichTextEditor value={post.bodyHtml} onChange={(v) => set("bodyHtml", v)} instanceKey={postId || "new"} /></Field>
        </div>
        <div className="stack-lg">
          <Card>
            <h3 className="t-lg" style={{ marginTop: 0 }}>Featured image</h3>
            <MediaUploaderField value={post.featuredImage} onChange={(v) => set("featuredImage", v)} path={`blog/${post.slug || "draft"}`} aspect="16/9" />
          </Card>
          <Card>
            <h3 className="t-lg" style={{ marginTop: 0 }}>Author</h3>
            <Field label="Author name"><Input value={post.authorName} onChange={(e) => set("authorName", e.target.value)} placeholder="Naa Dzama Sowah" /></Field>
            <label className="row" style={{ gap: 8, marginTop: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={!!post.authoredByMinor} onChange={(e) => set("authoredByMinor", e.target.checked)} />
              <span className="t-sm">Authored by a minor</span>
            </label>
          </Card>
        </div>
      </div>
    </AdminShell>
  );
}

export default AdminPostsListPage;
